/**
 * ============================================================================
 *  Alerte "nouvelle commande"
 * ============================================================================
 *
 *  Contrainte de conception : la personne entend ce son de 9h a 21h, des
 *  dizaines de fois par jour. Une sirene stridente finirait coupee au bout
 *  de deux jours. Il faut donc un son FORT mais AGREABLE.
 *
 *  Choix retenu : un carillon ascendant en accord majeur (do - mi - sol - do),
 *  timbre cloche. C'est le son universel de la bonne nouvelle — une caisse
 *  enregistreuse, une piece gagnee. Il porte loin sans agresser.
 *
 *  Pour la puissance sans distorsion : chaque note superpose une onde
 *  triangle (brillance, elle perce le bruit) et une sinusoide (corps), le
 *  tout passe dans un compresseur suivi d'un gain de rattrapage. C'est la
 *  technique qui rend un son percutant sans le faire saturer.
 *
 *  Les navigateurs interdisent de jouer un son tant que la personne n'a pas
 *  interagi avec la page : unlockOrderAlarm() doit etre appele sur un vrai
 *  geste. Repli sur /sounds/notification.mp3 si Web Audio est indisponible.
 */

let ctx: AudioContext | null = null;
let sortie: GainNode | null = null;
let boucle: number | null = null;
let arretAuto: number | null = null;
let enCours = false;
let replique: HTMLAudioElement | null = null;

/** Duree maximale sans acquittement, pour ne jamais sonner sans fin. */
const DUREE_MAX_MS = 60_000;
/** Intervalle entre deux carillons. Le son etant agreable, on peut respirer. */
const PERIODE_MS = 2_000;

/** Do majeur, octave haute : porte bien dans le bruit d'un magasin. */
const ARPEGE = [1046.5, 1318.5, 1568.0, 2093.0];
/** Ecart entre deux notes : assez serre pour rester vif. */
const ECART = 0.11;

/**
 * Deux alertes, deux timbres — et ce n'est pas une question de gout.
 *
 * Quand l'admin et le fournisseur sont dans la meme piece, un son identique
 * ne dit pas QUI doit agir : les deux se levent, ou aucun. Deux sons
 * distincts repondent a la question sans qu'on regarde un ecran.
 *
 *   admin        carillon majeur ASCENDANT — une vente vient d'entrer
 *   fournisseur  sonnette de porte DESCENDANTE — quelque chose a preparer
 *
 * Le sens de la melodie fait le travail : ca monte quand on encaisse, ca
 * descend quand on te livre un travail.
 */
export type TimbreAlerte = "admin" | "fournisseur";

/**
 * Sonnette de porte : sol puis do, une quarte descendante. Deux notes
 * seulement, tenues plus longtemps — moins vif que le carillon, et c'est
 * voulu : le fournisseur travaille les mains prises, il n'a pas besoin
 * qu'on le presse, il a besoin d'entendre.
 */
const SONNETTE = [784.0, 523.25];
/** Plus d'air entre les deux notes : c'est ce qui fait le « ding... dong ». */
const ECART_SONNETTE = 0.26;

/** Le motif et l'espacement, selon a qui l'alerte s'adresse. */
function motif(timbre: TimbreAlerte): { notes: number[]; ecart: number } {
  return timbre === "fournisseur"
    ? { notes: SONNETTE, ecart: ECART_SONNETTE }
    : { notes: ARPEGE, ecart: ECART };
}

function contexte(): AudioContext | null {
  if (typeof window === "undefined") return null;
  const Ctor: any =
    (window as any).AudioContext || (window as any).webkitAudioContext;
  if (!Ctor) return null;

  if (!ctx) {
    try {
      ctx = new Ctor();
    } catch {
      return null;
    }
  }

  if (ctx && !sortie) {
    // Compresseur puis gain de rattrapage : beaucoup plus present a l'oreille
    // qu'un simple volume pousse, et sans saturation.
    const comp = ctx.createDynamicsCompressor();
    comp.threshold.setValueAtTime(-20, ctx.currentTime);
    comp.knee.setValueAtTime(12, ctx.currentTime);
    comp.ratio.setValueAtTime(8, ctx.currentTime);
    comp.attack.setValueAtTime(0.002, ctx.currentTime);
    comp.release.setValueAtTime(0.2, ctx.currentTime);

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(2.6, ctx.currentTime);

    comp.connect(gain);
    gain.connect(ctx.destination);
    sortie = gain;

    // On garde une reference au compresseur en tete de chaine.
    (sortie as any).__entree = comp;
  }

  return ctx;
}

function entree(): AudioNode | null {
  if (!sortie) return null;
  return ((sortie as any).__entree as AudioNode) || sortie;
}

/**
 * A appeler depuis un vrai geste utilisateur (clic, touche).
 * Sans cela, aucun son ne peut etre joue.
 */
/**
 * iPhone : sans cette ligne, le bouton SILENCIEUX sur le cote du telephone
 * coupe tous les sons de l'appli — le fournisseur croit l'appli muette.
 * « playback » = on joue comme un lecteur de musique, meme en mode
 * silencieux (Safari 17+ ; ignore ailleurs).
 */
function sessionAudioLecture(): void {
  try {
    const s: any = (navigator as any).audioSession;
    if (s && s.type !== "playback") s.type = "playback";
  } catch {
    /* navigateur sans audioSession */
  }
}

export function unlockOrderAlarm(): void {
  sessionAudioLecture();
  const c = contexte();
  if (c && (c.state === "suspended" || (c.state as string) === "interrupted")) {
    c.resume().catch(() => {});
  }
  if (!replique) {
    try {
      replique = new Audio("/sounds/notification.mp3");
      replique.preload = "auto";
    } catch {
      replique = null;
    }
  }
}

/**
 * Une note de carillon : attaque quasi instantanee puis longue decroissance,
 * comme une cloche que l'on frappe.
 */
function note(c: AudioContext, freq: number, debut: number, duree: number) {
  const cible = entree();
  if (!cible) return;

  const g = c.createGain();
  g.gain.setValueAtTime(0.0001, debut);
  g.gain.exponentialRampToValueAtTime(0.85, debut + 0.008);
  g.gain.exponentialRampToValueAtTime(0.0001, debut + duree);
  g.connect(cible);

  // Triangle : la brillance qui perce le bruit ambiant.
  const brillance = c.createOscillator();
  brillance.type = "triangle";
  brillance.frequency.setValueAtTime(freq, debut);
  brillance.connect(g);
  brillance.start(debut);
  brillance.stop(debut + duree + 0.05);

  // Sinusoide a l'octave inferieure : le corps, la rondeur.
  const corps = c.createOscillator();
  const gCorps = c.createGain();
  corps.type = "sine";
  corps.frequency.setValueAtTime(freq / 2, debut);
  gCorps.gain.setValueAtTime(0.0001, debut);
  gCorps.gain.exponentialRampToValueAtTime(0.45, debut + 0.01);
  gCorps.gain.exponentialRampToValueAtTime(0.0001, debut + duree);
  corps.connect(gCorps);
  gCorps.connect(cible);
  corps.start(debut);
  corps.stop(debut + duree + 0.05);
}

/**
 * Un motif complet : les notes s'enchainent, la derniere est tenue.
 *
 * Le timbre par defaut reste « admin » : tous les appels ecrits avant
 * l'arrivee du fournisseur sonnent exactement comme avant.
 */
function carillon(timbre: TimbreAlerte = "admin"): boolean {
  const c = contexte();
  if (!c) return false;
  if (c.state === "suspended" || (c.state as string) === "interrupted") c.resume().catch(() => {});

  const { notes, ecart } = motif(timbre);
  // La sonnette tient sa derniere note plus longtemps : deux notes seules
  // ont besoin de resonner pour s'entendre de loin.
  const tenue = timbre === "fournisseur" ? 1.4 : 0.85;

  const t = c.currentTime + 0.02;
  notes.forEach((freq, i) => {
    const dernier = i === notes.length - 1;
    note(c, freq, t + i * ecart, dernier ? tenue : 0.5);
  });
  return true;
}

/** Repli si Web Audio est indisponible. */
function cycleReplique() {
  if (!replique) {
    try {
      replique = new Audio("/sounds/notification.mp3");
    } catch {
      return;
    }
  }
  try {
    replique.currentTime = 0;
    replique.volume = 1;
    replique.play().catch(() => {});
  } catch {
    /* silencieux : l'alerte visuelle reste */
  }
}

/**
 * Demarre l'alerte. Sans effet si elle sonne deja.
 *
 * `timbre` vaut « admin » par defaut : les appels existants ne changent pas.
 */
export function startOrderAlarm(timbre: TimbreAlerte = "admin"): void {
  if (enCours) return;
  enCours = true;

  const jouer = () => {
    if (!carillon(timbre)) cycleReplique();
  };

  jouer();
  boucle = window.setInterval(jouer, PERIODE_MS);
  arretAuto = window.setTimeout(stopOrderAlarm, DUREE_MAX_MS);
}

/** Arrete l'alerte et libere les minuteries. */
export function stopOrderAlarm(): void {
  enCours = false;
  if (boucle !== null) {
    clearInterval(boucle);
    boucle = null;
  }
  if (arretAuto !== null) {
    clearTimeout(arretAuto);
    arretAuto = null;
  }
  if (replique) {
    try {
      replique.pause();
      replique.currentTime = 0;
    } catch {
      /* rien */
    }
  }
}

export function alarmeEnCours(): boolean {
  return enCours;
}

/**
 * Le son est-il reellement autorise par le navigateur ?
 *
 * Chrome refuse tout son tant que la personne n'a pas interagi avec la page.
 * Sans ce test, l'alarme est muette et personne ne s'en rend compte : on croit
 * qu'elle fonctionne, et on rate des commandes. On expose donc l'etat reel
 * pour pouvoir prevenir a l'ecran.
 */
export function sonAutorise(): boolean {
  if (typeof window === "undefined") return false;
  const Ctor: any =
    (window as any).AudioContext || (window as any).webkitAudioContext;
  if (!Ctor) return false;
  if (!ctx) return false;
  return ctx.state === "running";
}

/** Joue un seul motif, pour verifier le son sans declencher l'alarme. */
export function testerOrderAlarm(timbre: TimbreAlerte = "admin"): void {
  unlockOrderAlarm();
  if (!carillon(timbre)) cycleReplique();
}


/**
 * ===========================================================================
 *  L'ALERTE DES DEMANDES FOURNISSEURS (25/10/2026)
 * ===========================================================================
 *
 *  « On ne les remarque pas » : une demande (article manquant, retrait,
 *  achat caisse...) arrivait sans bruit. Elle a desormais SON son, qu'on ne
 *  confond pas avec une commande : trois bips aigus et rapides puis une note
 *  grave — « bip-bip-bip, boum ». Le motif est joue trois fois.
 *
 *  Il ne touche pas a l'alarme des commandes : les deux peuvent sonner.
 * ===========================================================================
 */
const BIPS = [1760.0, 1760.0, 1760.0, 659.25];
const ECART_BIPS = 0.14;

function motifDemande(): boolean {
  const c = contexte();
  if (!c) return false;
  if (c.state === "suspended" || (c.state as string) === "interrupted") c.resume().catch(() => {});
  const t = c.currentTime + 0.02;
  BIPS.forEach((freq, i) => {
    const dernier = i === BIPS.length - 1;
    note(c, freq, t + i * ECART_BIPS + (dernier ? 0.08 : 0), dernier ? 0.9 : 0.12);
  });
  return true;
}

/** Joue l'alerte d'une nouvelle demande : le motif, trois fois. */
export function jouerAlerteDemande(fois = 3): void {
  for (let i = 0; i < fois; i++) {
    window.setTimeout(() => {
      if (!motifDemande()) cycleReplique();
    }, i * 1600);
  }
}
